import BaseController from './base';

export default class extends BaseController {
  // Use an explicit target when you want to focus on an element that is not a form element.
  //    E.g., an error message in a div. Make sure it's focusable (e.g. set tabindex="0")
  //    Example usage: _employee_checkbox_table.html.erb
  // Use the focus override when the original target is not reachable
  //    or if the target is not the source of data
  //    Example usage: persona's verification company/information_Requests/edit.html.erb
  // Otherwise, the controller will look for a simpleform input with an error (.is-invalid)
  static targets = ['explicit', 'override'];
  static values = {
    debug: { Type: Boolean, default: false },
    timeout: { Type: Number, default: 0 },
    storageKey: { Type: String, default: '' },
  };

  connect() {
    if (this.storageKeyValue) {
      // storageKey mode restores focus to the explicit target after a full-page navigation
      // (e.g. tab switch or sort click). It intentionally skips form-error focus and
      // mutation observation, which are only relevant for the default form-validation path.
      this.restoreFocus();
    } else {
      // wrapping in `requestAnimationFrame` is a hack to help make sure the focus happens after other stimulus
      // controllers have played with the DOM.
      requestAnimationFrame(() => this.handleFocus());
      this.observeMutations();
    }
  }

  restoreFocus() {
    try {
      if (sessionStorage.getItem(this.storageKeyValue)) {
        sessionStorage.removeItem(this.storageKeyValue);
        if (this.hasExplicitTarget) {
          this.explicitTarget.focus();
        }
      }
    } catch {
      // sessionStorage may not be available in sandboxed iframes
    }
  }

  trackFocus() {
    try {
      sessionStorage.setItem(this.storageKeyValue, 'true');
    } catch {
      // sessionStorage may not be available in sandboxed iframes
    }
  }

  handleFocus() {
    this.focusOnField(this.firstInDomOrder(this.formInput(), ...this.explicitTargets));
  }

  firstInDomOrder(...elements) {
    this.debug('elements', elements);
    const filtered = elements.filter((el) => el instanceof Element);

    if (filtered.length === 0) {
      return;
    }

    filtered.sort((a, b) => {
      if (a === b) {
        return 0;
      }
      const pos = a.compareDocumentPosition(b);
      return pos & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
    });

    return filtered[0];
  }

  observeMutations() {
    // TL;DR: We only care about new nodes containing form errors
    this.observer = new MutationObserver((mutations) => {
      const errorDetected = mutations.some(
        ({ type, addedNodes }) =>
          type === 'childList' && Array.from(addedNodes).some((node) => this.newNodeHasError(node)),
      );

      if (errorDetected) {
        this.debug('Mutation observer triggered');
        this.handleFocus();
      }
    });

    this.observer.observe(this.element, { childList: true, subtree: true });
  }

  newNodeHasError(node) {
    return (
      node instanceof HTMLElement &&
      (node.matches('.invalid-feedback') ||
        node.querySelector('.invalid-feedback') ||
        node.matches('[data-focus-target]') ||
        node.querySelector('[data-focus-target]'))
    );
  }

  formInput() {
    this.assignErrorToOverride();

    const firstFormInput = Array.from(this.element.querySelectorAll('.is-invalid')).find(
      this.isVisible,
    );

    if (this.hasOverrideTarget && this.overrideTarget.dataset.overrideFor === firstFormInput?.id) {
      return this.overrideTarget;
    }

    return firstFormInput;
  }

  assignErrorToOverride() {
    if (this.hasOverrideTarget) {
      const overrideFor = document.getElementById(this.overrideTarget.dataset.overrideFor);
      if (overrideFor?.classList.contains('is-invalid')) {
        this.overrideTarget.setAttribute(
          'aria-describedby',
          overrideFor.getAttribute('aria-describedby'),
        );
        this.overrideTarget.classList.add('is-invalid');
      }
    }
  }

  focusOnField(element) {
    if (element) {
      this.debug('Focus on field', element);
      this.debug('Timeout', this.timeoutValue);
      setTimeout(() => element?.focus(), this.timeoutValue);
    } else {
      this.debug('No element to focus on');
    }
  }

  isVisible(el) {
    if (!el) {
      return false;
    }

    const style = window.getComputedStyle(el);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      el.offsetParent !== null &&
      !el.disabled
    );
  }

  debug(message, ...optionalParams) {
    if (this.debugValue) {
      console.info(message, ...optionalParams);
    }
  }
}
